import { PhotoOutlined } from '@mui/icons-material';
import { memo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import OWDropZone from 'components/design/ow-drop-zone';
import { FILE_TYPES } from 'constants/logo';

type LogoDropZoneProps = {
    logo?: string;
    onChange: (newLogo: string) => void;
};

const LogoDropZone = memo(
    ({ logo, onChange }: LogoDropZoneProps) => {
        const { t } = useTranslation();
        const [hover, setHover] = useState(false);
        const [dragging, setDragging] = useState(false);

        return (
            <OWDropZone
                accept={FILE_TYPES}
                onChange={onChange}
                onDragEnter={() => setDragging(true)}
                onDragLeave={() => setDragging(false)}
            >
                <Stack
                    data-testid="hover-area"
                    direction="row"
                    borderRadius={2}
                    onPointerEnter={() => setHover(true)}
                    onPointerLeave={() => setHover(false)}
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        aspectRatio: '1',
                        height: '127.75px',
                        border: '1px dashed rgba(0,0,0,0.23)',
                    }}
                >
                    {logo && <img src={logo} style={{ position: 'absolute' }} />}
                    {((logo !== undefined && hover) || dragging) && (
                        <Stack
                            data-testid="hover-overlay"
                            borderRadius={2}
                            alignItems="center"
                            justifyContent="center"
                            sx={(theme) => ({
                                backgroundColor: theme.palette.primary.main,
                                color: theme.palette.common.white,
                                opacity: '66%',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                            })}
                        >
                            {dragging && t('COMMON.DROP')}
                        </Stack>
                    )}
                    {(logo === undefined || hover) && !dragging && (
                        <Stack
                            position="absolute"
                            alignItems="center"
                            width="100%"
                            height="100%"
                            justifyContent="center"
                            paddingInline={1}
                            zIndex={2}
                        >
                            <PhotoOutlined htmlColor={logo ? 'white' : 'black'} />
                            <Stack
                                width="min-content"
                                minWidth="100%"
                                color={(theme) => (logo ? theme.palette.common.white : theme.palette.common.black)}
                            >
                                <Typography textAlign="center" variant="body3">
                                    <Box component="span" fontWeight="700" sx={{ textDecoration: 'underline' }}>
                                        {t('COMMON.DROP_ZONE_1')}
                                    </Box>{' '}
                                    {t('COMMON.DROP_ZONE_2')}
                                </Typography>
                                <Typography textAlign="center" variant="body3">
                                    {t('COMMON.DROP_ZONE_FILE_TYPES')}
                                </Typography>
                            </Stack>
                        </Stack>
                    )}
                </Stack>
            </OWDropZone>
        );
    },
    (prev, next) => prev.logo === next.logo,
);

export default LogoDropZone;
